import React, { forwardRef, useContext, useEffect, useState } from "react";
import { Row, Col, Form, Card, CardGroup } from "react-bootstrap";
import {
  URN,
  DIABETES_TYPE,
  GESTATION_DIABETES_WK,
  SALUTATION,
  GIVEN_NAME,
  FAMILY_NAME,
  DOB,
  PRE_PREGNANCY_WEIGHT,
  HEIGHT,
  MOBILE_NUMBER,
  EMAIL,
  INDIGENOUS_STATUS,
  DUE_DATE,
  BIRTH_PLACE,
  MANAGEMENT_TYPE,
  LANGUAGE,
  BMI,
  ETHNICITY,
  INSULIN,
  METFORMIN,
  DIET,
  TEAM,
  GESTATION_ENROL_WK,
  OGTT,
  RESEARCH,
  GENERATE_ALERTS,
  STUDY_ID,
  RANDOMISED_DATE
} from "../../util/const";
import { PatientContext } from "../ActivePatients";
import { ValidationContext } from "../PatientForm";
import Papa, { ParseResult } from "papaparse";
import { CalculateBMI, CalculateDiagnosisDate } from "../../util/utils";


const PatientDetails = forwardRef<HTMLFormElement>((props,ref) => {

  const context = useContext(PatientContext);
  const patient = context.patient;
  const validationContext = useContext(ValidationContext);

  // Clinical
  const [urn, setUrn] = useState(patient.urn);
  const [team, setTeam] = useState(patient.team);
  const [group, setGroup] = useState(patient.group);
  const [diabetesType, setDiabetesType] = useState(patient.diabetesType);
  const [diabetesGestation, setDiabetesGestation] = useState(patient.diabetesGestation);
  const [currentGestation, setCurrentGestation] = useState(patient.currentGestation);
  const [dueDate, setDueDate] = useState(patient.dueDate);
  const [ogttFasting, setOgttFasting] = useState(patient.ogttFasting);
  const [ogttOneHour, setOgttOneHour] = useState(patient.ogttOneHour);
  const [ogttTwoHour, setOgttTwoHour] = useState(patient.ogttTwoHour);
  
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [managementType, setManagementType] = useState(patient.managementType);

  // Demographics
  const [salutation, setSalutation] = useState(patient.salutation);
  const [givenName, setGivenName] = useState(patient.givenNames);
  const [familyName, setFamilyName] = useState(patient.familyName);
  const [dob, setDob] = useState(patient.birthDate);
  const [birthPlace, setBirthPlace] = useState(patient.birthPlace);
  const [weight, setWeight] = useState(patient.weight);
  const [height, setHeight] = useState(patient.height);
  const [email, setEmail] = useState(patient.email);
  const [mobile, setMobile] = useState(patient.phone);
  const [indigenous, setIndigenous] = useState(patient.indigenousStatus);
  const [language, setLanguage] = useState(patient.language);
  const [ethnicity, setEthnicity] = useState(patient.ethnicity);
  
  const [studyId, setStudyId] = useState(patient.studyId);
  const [randomisedDate, setRandomisedDate] = useState(patient.randomisedDate);
  
  const [countries, setCountries] = useState<string[]>([]);
  const [ethnicities, setEthnicities] = useState<string[]>([]);
  const [languages, setLanguages] = useState<string[]>([]);

  useEffect(() => {
    Papa.parse("/countries.csv", {
      header: false,
      download: true,
      skipEmptyLines: true,
      delimiter: ",",
      complete: (results: ParseResult<string>) => {
        setCountries(results.data)
      },
    })
  }, []);

  useEffect(() => {
    Papa.parse("/ethnicities.csv", {
      header: false,
      download: true,
      skipEmptyLines: true,
      delimiter: ",",
      complete: (results: ParseResult<string>) => {
        setEthnicities(results.data)
      },
    })
  }, []);

  useEffect(() => {
    Papa.parse("/languages.csv", {
      header: false,
      download: true,
      skipEmptyLines: true,
      delimiter: ",",
      complete: (results: ParseResult<string>) => {
        setLanguages(results.data)
      },
    })
  }, []);

  const handleTypeCheckChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    patient.diabetesType = e.target.value;
    setDiabetesType(patient.diabetesType);
  }

  const handleMetforminCheckChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      if(patient.managementType.includes(DIET) || patient.managementType.length === 0) {
        patient.managementType = [METFORMIN];
      } else {
        let index = patient.managementType.indexOf(INSULIN);
        if(index > -1) {
          patient.managementType = [METFORMIN, INSULIN];
        }
      } 
    } else {
      let index = patient.managementType.indexOf(INSULIN);
      index > -1 ? patient.managementType = [INSULIN] : patient.managementType = [];
    }
    setManagementType(patient.managementType);
  }

  const handleDietCheckChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    patient.managementType = [];
    if (e.target.checked)
    {
      patient.managementType = [DIET];
    } 
    setManagementType(patient.managementType);
  };

  const handleInsulinCheckChange = (e: React.ChangeEvent<HTMLInputElement>) => {

    if (e.target.checked) {
      if(patient.managementType.includes(DIET) || patient.managementType.length === 0) {
        patient.managementType = [INSULIN];
      } else {
        let index = patient.managementType.indexOf(METFORMIN);
        if(index > -1) {
          patient.managementType = [METFORMIN, INSULIN];
        }
      } 
    } else {
      let index = patient.managementType.indexOf(METFORMIN);
      index > -1 ? patient.managementType = [METFORMIN] : patient.managementType = [];
    }
    setManagementType(patient.managementType);
  };
  
  return (
    <Form noValidate validated={validationContext.hasValidated} ref={ref} >
      <Row>
        <CardGroup className="mb-3">
        <Card>
            <Card.Header>The Patient</Card.Header>
            <Card.Body>
              <Card style={{ border:0 }}>
                <Row>
                <Form.Group as={Col} sm={2} controlId="studyId">
                  <Form.FloatingLabel label={STUDY_ID} className="text-secondary">
                    <Form.Control
                      type="text"
                      value={studyId} 
                      onChange={(e) => { patient.studyId = e.target.value; setStudyId(e.target.value); }}
                      isInvalid={isNaN(Number(patient.studyId)) || patient.studyId.length !== 4}
                      required />
                  </Form.FloatingLabel>
                </Form.Group>
                <Form.Group as={Col} sm={3} controlId="randomisedDate">
                  <Form.FloatingLabel label={RANDOMISED_DATE} className="text-secondary">
                    <Form.Control
                      type="date"
                      placeholder=""
                      value={randomisedDate}
                      onChange={(e) => { setRandomisedDate(e.target.value); patient.randomisedDate = e.target.value; }}
                      required />
                  </Form.FloatingLabel> 
                </Form.Group>
                <Form.Group as={Col} sm={2} controlId="research">
                  <Form.FloatingLabel label={RESEARCH} className="text-secondary">
                    <Form.Select
                      value={group}
                      onChange={(e) => {
                        setGroup(e.target.value);
                        patient.group = e.target.value;
                      }}
                      required>
                      <option value=""></option>
                      <option value="Intervention">Intervention</option>
                      <option value="Control">Control</option>
                      <option value="Neither">Neither</option>
                    </Form.Select>
                  </Form.FloatingLabel>
                </Form.Group>
                <Form.Group as={Col} sm={3} className="mt-2" controlId="override">
                  <Form.Check
                    id="switch-override"
                    type="switch"
                    readOnly
                    checked={group==="Intervention"}
                    label={GENERATE_ALERTS}
                  />
                </Form.Group>
              </Row>
              <Row>
                <Form.Group as={Col} sm={3} className="mt-3" controlId="urn">
                  <Form.FloatingLabel label={URN} className="text-secondary">
                    <Form.Control
                      type="text"
                      value={urn}
                      onChange={(e) => { setUrn(e.target.value); patient.urn = e.target.value; }}
                      required />
                  </Form.FloatingLabel>
                </Form.Group>
                <Form.Group as={Col} sm={2} className="mt-3" controlId="team">
                  <Form.FloatingLabel label={TEAM} className="text-secondary">
                    <Form.Select
                      value={team}
                      onChange={(e) => {
                        setTeam(e.target.value);
                        patient.team = e.target.value;
                      }}
                      required>
                      <option value=""></option>
                      <option value="Orange">Orange</option>
                      <option value="Yellow">Yellow</option>
                      <option value="Blue">Blue</option>
                      <option value="Purple">Purple</option>
                      <option value="Diamond">Diamond</option>
                    </Form.Select>
                  </Form.FloatingLabel>
                </Form.Group>
              </Row>
              <Row>
                <Form.Group as={Col} sm={2} className="mt-3" controlId="salutation">
                  <Form.FloatingLabel label={SALUTATION} className="text-secondary">
                    <Form.Select
                      value={salutation == null ? "" : salutation}
                      onChange={(e) => {
                        setSalutation(e.target.value);
                        patient.salutation = e.target.value;
                      }}
                      >
                      <option value=""></option>
                      <option value="Mrs">Mrs</option>
                      <option value="Miss">Miss</option>
                      <option value="Ms">Ms</option>
                      <option value="Dr">Dr</option>
                      <option value="Mr">Mr</option>
                    </Form.Select>
                  </Form.FloatingLabel>
                </Form.Group>
                <Form.Group as={Col} className="mt-3" controlId="givenName">
                  <Form.FloatingLabel label={GIVEN_NAME} className="text-secondary">
                    <Form.Control
                      type="text"
                      value={givenName}
                      onChange={(e) => { setGivenName([e.target.value]); patient.givenNames = [e.target.value]; } }
                      required />
                  </Form.FloatingLabel>
                </Form.Group>
                <Form.Group as={Col} className="mt-3" controlId="familyName">
                  <Form.FloatingLabel label={FAMILY_NAME} className="text-secondary">
                    <Form.Control
                      type="text"
                      value={familyName}
                      onChange={(e) => { setFamilyName(e.target.value); patient.familyName = e.target.value; }}
                      required />
                  </Form.FloatingLabel>
                </Form.Group>
              </Row>
              <Row>
                <Form.Group as={Col} sm={3} className="mt-3" controlId="dob">
                  <Form.FloatingLabel label={DOB} className="text-secondary">
                    <Form.Control
                      type="date"
                      placeholder=""
                      value={dob}
                      onChange={(e) => { setDob(e.target.value); patient.birthDate = e.target.value; }}
                      required />
                  </Form.FloatingLabel> 
                </Form.Group>
                <Form.Group as={Col} className="mt-3" controlId="birthPlace">
                  <Form.FloatingLabel label={BIRTH_PLACE} className="text-secondary">
                    <Form.Select
                      value={birthPlace}
                      onChange={(e) => {
                        setBirthPlace(e.target.value);
                        patient.birthPlace = e.target.value;
                      }}
                      >
                      <option value=""></option>
                      {countries.map((value) => (
                      <React.Fragment key={value}>
                      <option value={value}>
                        {value}
                      </option>
                      </React.Fragment>
                      ))}
                    </Form.Select>
                  </Form.FloatingLabel>
                </Form.Group>
                <Form.Group as={Col} className="mt-3" controlId="language">
                  <Form.FloatingLabel label={LANGUAGE} className="text-secondary">
                    <Form.Select
                      value={language}
                      onChange={(e) => {
                        setLanguage(e.target.value);
                        patient.language = e.target.value;
                      }}
                      required>
                      <option value=""></option>
                      {languages.map((value) => (
                      <React.Fragment key={value}>
                      <option value={value}>
                        {value}
                      </option>
                      </React.Fragment>
                      ))}
                    </Form.Select>
                  </Form.FloatingLabel> 
                </Form.Group>
                <Form.Group as={Col} className="mt-3" controlId="ethnicity">
                  <Form.FloatingLabel label={ETHNICITY} className="text-secondary">
                    <Form.Select
                      value={ethnicity}
                      onChange={(e) => {
                        setEthnicity(e.target.value);
                        patient.ethnicity = e.target.value;
                      }}
                      >
                      <option value=""></option>
                      {ethnicities.map((value) => (
                      <React.Fragment key={value}>
                      <option value={value}>
                        {value}
                      </option>
                      </React.Fragment>
                      ))}
                    </Form.Select>
                  </Form.FloatingLabel> 
                </Form.Group>
              </Row>
              <Row className="d-flex align-items-center">
                <Form.Group as={Col} sm={3} className="mt-3" controlId="mobile">
                  <Form.FloatingLabel label={MOBILE_NUMBER} className="text-secondary">
                    <Form.Control
                      type="text"
                      value={mobile}
                      onChange={(e) => { setMobile(e.target.value); patient.phone = e.target.value; }}
                      required />
                  </Form.FloatingLabel>
                </Form.Group>
                <Form.Group as={Col} className="mt-3" controlId="email">
                  <Form.FloatingLabel label={EMAIL} className="text-secondary">
                    <Form.Control
                      type="email"
                      value={email}
                      onChange={(e) => { setEmail(e.target.value); patient.email = e.target.value; }}
                      required />
                  </Form.FloatingLabel>  
                </Form.Group>
              </Row>
              <Row>
                <Form.Group as={Col} className="mt-3" controlId="indigenous">
                  <Form.FloatingLabel label={INDIGENOUS_STATUS} className="text-secondary">
                    <Form.Select
                      value={indigenous == null ? '' : indigenous}
                      onChange={(e) => { setIndigenous(e.target.value); patient.indigenousStatus = e.target.value; }}
                      >
                      <option></option>
                      <option value="Aboriginal">Aboriginal</option>
                      <option value="Torres Strait Islander">Torres Strait Islander</option>
                      <option value="Aboriginal & Torres Strait Islander">Aboriginal & Torres Strait Islander</option>
                      <option value="None">None</option>
                      <option value="Not Stated">Not Stated</option>
                    </Form.Select>
                  </Form.FloatingLabel>
                </Form.Group>
              </Row>
              </Card>
              <Card className="mt-3 emphasise">
                <Card.Body>
                  <Row>
                    <Form.Group as={Col} controlId="dueDate">
                      <Form.FloatingLabel label={DUE_DATE} className="text-secondary">
                        <Form.Control
                          type="date"
                          value={dueDate}
                          onChange={(e) => { setDueDate(e.target.value); patient.dueDate = e.target.value; }}
                          required>
                        </Form.Control>
                      </Form.FloatingLabel>
                    </Form.Group>
                    <Form.Group as={Col} controlId="currentGestation">
                      <Form.FloatingLabel label={GESTATION_ENROL_WK} className="text-secondary">
                        <Form.Control
                          type="number"
                          step="1"
                          max="40"
                          value={currentGestation}
                          onChange={(e) => { setCurrentGestation(e.target.value); patient.currentGestation = e.target.value; }}
                          required>
                        </Form.Control>
                      </Form.FloatingLabel>
                    </Form.Group>
                    <Form.Group as={Col} controlId="diabetesGestation">
                      <Form.FloatingLabel label={GESTATION_DIABETES_WK} className="text-secondary">
                        <Form.Control
                          type="number"
                          step="1"
                          max="40"
                          value={diabetesGestation}
                          onChange={(e) => { setDiabetesGestation(e.target.value); patient.diabetesGestation = e.target.value;}}
                          required>
                        </Form.Control>
                      </Form.FloatingLabel>
                    </Form.Group>
                    <Form.Group as={Col} controlId="dod">
                      <Form.FloatingLabel label="Diagnosed" className="text-secondary">
                        <Form.Control
                          type="text"
                          value={patient.diagnosisDate = CalculateDiagnosisDate(patient.startDate, patient.currentGestation, patient.diabetesGestation)}
                          readOnly
                        />
                      </Form.FloatingLabel> 
                    </Form.Group>
                  </Row>
                </Card.Body>
              </Card>
              
              <Card className="mt-3 emphasise">
                <Card.Body>
                  <Row className="mt-2 middle">
                    <Form.Group as={Col} className="mt-2" controlId="weight">
                      <Form.FloatingLabel label={PRE_PREGNANCY_WEIGHT} className="text-secondary">
                        <Form.Control
                          type="number"
                          pattern="[1-9]{1}[0-9]{1,2}(\.[1-9])?"
                          value={weight}
                          onChange={(e) => { setWeight(e.target.value); patient.weight = e.target.value; }}
                        />
                      </Form.FloatingLabel>
                    </Form.Group>
                    <Form.Group as={Col} className="mt-2" controlId="height">
                      <Form.FloatingLabel label={HEIGHT} className="text-secondary">
                        <Form.Control
                          type="number"
                          step="1"
                          value={height}
                          onChange={(e) => { setHeight(e.target.value); patient.height = e.target.value; }}
                        />
                      </Form.FloatingLabel> 
                    </Form.Group>
                    <Form.Group as={Col} className="mt-2" controlId="bmi">
                      <Form.FloatingLabel label={BMI} className="text-secondary">
                        <Form.Control
                          type="number"
                          value={CalculateBMI(height,weight)}
                          readOnly
                        />
                      </Form.FloatingLabel> 
                    </Form.Group>
                  </Row>
                </Card.Body>
              </Card>
            </Card.Body>
            <Card.Footer>
            </Card.Footer>
          </Card>
          <Card>
            <Card.Header>Diabetes Management</Card.Header>
            <Card.Body>
              <Row>
                <Col className="mt-2 mb-2">
                  <Form.Label className="color-text">{DIABETES_TYPE}</Form.Label>
                  <div key={`diabetes-radio`}>
                    <Form.Check
                      inline
                      label="GDM"
                      value="GDM"
                      name="group-diabetes-type"
                      type={`radio`}
                      id={`diabetes-radio-gdm`}
                      onChange={handleTypeCheckChange}
                      checked={diabetesType === "GDM"}
                      required
                    />
                    <Form.Check
                      inline
                      label="T2DM"
                      value="T2DM"
                      name="group-diabetes-type"
                      type={`radio`}
                      id={`diabetes-radio-t2dm`}
                      onChange={handleTypeCheckChange}
                      checked={diabetesType === "T2DM"}
                      required
                    />
                    <Form.Check
                      inline
                      label="T1DM"
                      value="T1DM"
                      name="group-diabetes-type"
                      type={`radio`}
                      id={`diabetes-radio-t1dm`}
                      onChange={handleTypeCheckChange}
                      checked={diabetesType === "T1DM"}
                      required
                    />
                  </div>
                </Col>
                <Col className="mt-2 mb-2">
                  <Form.Label className="color-text">{OGTT}</Form.Label>
                  <Row className="middle">
                    <Form.Group as={Col} controlId="ogttFasting">
                      <Form.Control
                        value={ogttFasting}
                        onChange={(e) => {
                          setOgttFasting(e.target.value);
                          patient.ogttFasting = e.target.value;
                        }}
                      />
                    </Form.Group>
                    /
                    <Form.Group as={Col} controlId="ogttOneHour">
                      <Form.Control 
                        value={ogttOneHour}
                        onChange={(e) => {
                          setOgttOneHour(e.target.value);
                          patient.ogttOneHour = e.target.value;
                        }}
                        />
                    </Form.Group>
                    /
                    <Form.Group as={Col} controlId="ogttTwoHour">
                      <Form.Control 
                        value={ogttTwoHour}
                        onChange={(e) => {
                          setOgttTwoHour(e.target.value);
                          patient.ogttTwoHour = e.target.value;
                        }}
                        />
                    </Form.Group>
                  </Row>
                </Col>
              </Row>
              <Row>
                <Col>
                  <Form.Label className="color-text">{MANAGEMENT_TYPE}</Form.Label>
                  <Row>
                    <Form.Group as={Col} controlId="diet" className="mb-3">
                      <Form.Check
                        label={DIET}
                        name="group-management-type"
                        type={`checkbox`}
                        id={`diet-check`}
                        onChange={handleDietCheckChange}
                        checked={patient.managementType.includes(DIET)}
                      />
                    </Form.Group>
                    <Form.Group as={Col} controlId="metformin">
                      <Form.Check
                        label={METFORMIN}
                        name="group-management-type"
                        type={`checkbox`}
                        id={`metformin-check`}
                        onChange={handleMetforminCheckChange}
                        checked={patient.managementType.includes(METFORMIN)}
                      />
                    </Form.Group>
                    <Form.Group as={Col} controlId="insulin">
                      <Form.Check
                        label={INSULIN}
                        name="group-management-type"
                        type={`checkbox`}
                        id={`insulin-check`}
                        onChange={handleInsulinCheckChange}
                        checked={patient.managementType.includes(INSULIN)}
                      />
                    </Form.Group>
                    <Col sm={5}></Col>
                  </Row>
                </Col>
              </Row>
            </Card.Body>
            <Card.Footer>
            </Card.Footer>
          </Card>
        </CardGroup>
      </Row>
    </Form> 
  );
});

export default PatientDetails;